import React, {
    createContext,
    useContext,
    ReactNode,
    useState,
    useEffect,
} from 'react';
import {
    defaultFilterTypes,
    IFilterOptionState,
    sortDirections,
} from '../../types/lists/listTypes';
import {
    IChannelMannagerState,
    IFiltersState,
} from '../../types/channelManager/channelManagerTypes';
import { buildEndPoint, mapFiltersToOdata } from '../../utils/odataEndPoint';
import { readOnlyEntities } from '../../utils/commonVariables/entityKeys';
import api from '../../services/api';
import { IChannelManager } from '../../types/entities/IChannelManager';
import useLanguage from '../../customHooks/useLanguage';
import { useNotification } from '../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../utils/commonVariables/messages';

const FILTERS_INITIAL_STATE: IFiltersState = {
    Name: {
        filterType: defaultFilterTypes.DEFAULT_STRING_FILTER_TYPE,
        filterKey: 'Name',
        value: '',
    },
    IdChannelManagerStatusType: {
        value: null,
        filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
        filterKey: 'ChannelManagerStatus/IdChannelManagerStatusType',
    },
    IdExternalIntegrationType: {
        value: null,
        filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
        filterKey: 'IdExternalIntegrationType',
    },
};

interface ChannelManagersContextType {
    filters: IFiltersState;
    handleFilterChange: (
        filterKey: string,
        value: string | number | null | IFilterOptionState
    ) => void;
    handleSearch: (pageNumber?: number) => Promise<void>;
    resetFilters: () => void;
    items: IChannelMannagerState[];
    sortedBy: string;
    sortDir: sortDirections;
    sortByField: (field: string) => void;
    currentResults: number;
    totalResults: number;
    isLoading: boolean;
    handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

const ContextChannelManagers = createContext<
    ChannelManagersContextType | undefined
>(undefined);

export const ProviderChannelManagers = ({
    children,
}: {
    children: ReactNode;
}) => {
    const language = useLanguage();
    const { setErrorMessage } = useNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [sortDir, setSortDir] = useState<sortDirections>(sortDirections.ASC);
    const [sortedBy, setSortedBy] = useState('Name');
    const resultsPerPage = 40;
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    const [items, setItems] = useState<IChannelMannagerState[]>([]);

    const [filters, setFilters] = useState(FILTERS_INITIAL_STATE);

    const handleFilterChange = (
        filterKey: string,
        value: string | number | null | IFilterOptionState
    ) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [filterKey]: {
                ...prevFilters[filterKey as keyof IFiltersState],
                value,
            },
        }));
    };

    const resetFilters = () => {
        setFilters(FILTERS_INITIAL_STATE);
        getChannelManagers(1, FILTERS_INITIAL_STATE);
    };

    const getChannelManagers = async (
        pageNumber = 1,
        filtersState = filters
    ) => {
        setPage(pageNumber);
        if (pageNumber === 1) setItems([]);
        const filtersToOdata = mapFiltersToOdata(filtersState);
        const endPoint = buildEndPoint(
            {
                entityName: readOnlyEntities.ChannelManager,
                query: {
                    select: [
                        'Id',
                        'Name',
                        'ChannelManagerStatus/IdChannelManagerStatusType',
                        'ChannelManagerStatus/ChannelManagerStatusType/LocalizedCaption',
                        'ChannelManagerStatus/ChannelManagerStatusType/Caption',
                        'ChannelManagerStatus/ChannelManagerStatusType/Name',
                        'ChannelManagerStatus/ChannelManagerStatusType/Color',
                        'IdExternalIntegrationType',
                        'ExternalIntegrationType/LocalizedCaption',
                        'ExternalIntegrationType/Caption',
                        'ExternalIntegrationType/Name',
                    ],
                    expand: [
                        `${readOnlyEntities.ChannelManagerStatus}/${readOnlyEntities.ChannelManagerStatusType}`,
                        readOnlyEntities.ExternalIntegrationType,
                    ],
                    filter: [
                        `ChannelManagerStatus/ChannelManagerStatusType/Key ne 'LOST'`,
                        'Active eq true',
                        ...filtersToOdata,
                    ],
                    orderBy: `${sortedBy} ${sortDir}`,
                    top: resultsPerPage,
                },
            },
            pageNumber
        );
        setIsLoading(true);
        try {
            const { data } = await api.get(endPoint);
            const { value: _items } = data;
            const _totalResults = parseInt(data['odata.count']);

            const newItems: IChannelMannagerState[] = _items.map(
                (item: IChannelManager) => ({
                    Id: item.Id,
                    Name: item.Name,
                    IdStatus:
                        item.ChannelManagerStatus?.IdChannelManagerStatusType,
                    Status:
                        item.ChannelManagerStatus?.ChannelManagerStatusType
                            ?.LocalizedCaption ||
                        item.ChannelManagerStatus?.ChannelManagerStatusType
                            ?.Caption ||
                        item.ChannelManagerStatus?.ChannelManagerStatusType
                            ?.Name,
                    StatusColor:
                        item.ChannelManagerStatus?.ChannelManagerStatusType
                            ?.Color,
                    ExternalIntegrationType:
                        item.ExternalIntegrationType?.LocalizedCaption ||
                        item.ExternalIntegrationType?.Caption ||
                        item.ExternalIntegrationType?.Name,
                    IdExternalIntegrationType: item.IdExternalIntegrationType,
                })
            );

            if (pageNumber === 1) {
                setItems(newItems);
                setTotalResults(_totalResults);
            } else {
                setItems(prev => [...prev, ...newItems]);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getChannelManagers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, sortedBy, sortDir]);

    const handleSearch = async (pageNumber = 1) => {
        await getChannelManagers(pageNumber);
    };

    const handleSortByField = (field: string) => {
        if (field === sortedBy) {
            setSortDir(prevSortDir =>
                prevSortDir === sortDirections.ASC
                    ? sortDirections.DESC
                    : sortDirections.ASC
            );
        } else {
            setSortedBy(field);
            setSortDir(sortDirections.ASC);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isOnBottom = scrollTop > scrollHeight - clientHeight - 1;
        if (isOnBottom && !isLoading && page < totalPages) {
            const nextPage = page + 1;
            setPage(nextPage);
            getChannelManagers(nextPage);
        }
    };

    return (
        <ContextChannelManagers.Provider
            value={{
                filters,
                handleFilterChange,
                items,
                handleSearch,
                resetFilters,
                currentResults: items.length,
                totalResults,
                isLoading,
                sortedBy,
                sortDir,
                sortByField: handleSortByField,
                handleScroll,
            }}
        >
            {children}
        </ContextChannelManagers.Provider>
    );
};

export const useChannelManagers = () => {
    const context = useContext(ContextChannelManagers);
    if (context === undefined) {
        throw new Error(
            'useChannelManagers must be used within a ProviderChannelManagers'
        );
    }
    return context;
};
